/**
 * WSC Add-to-Cart Diagnostic
 * ============================================================================
 * A read-only audit of every link in the configurator's add-to-cart chain.
 * It NEVER adds to cart, submits a form, or clicks anything — it only inspects
 * the DOM + the configurator state and reports which link is broken.
 *
 * WHY THIS EXISTS
 * ---------------
 * The configurator does not POST to /cart/add.js itself. It syncs the customer's
 * selections into the ACO/WCPA app's hidden fields, then clicks the THEME's real
 * "Add to Cart" submit button. That chain has several silent failure points:
 *
 *   1. No  <form action="/cart/add">           → nothing to submit into
 *   2. No  theme submit button (or disabled)   → our click() hits nothing
 *   3. ACO/WCPA app didn't load                → required option fields never exist
 *   4. acoReady never flips true (10s timeout) → the Add-to-Cart button is a
 *                                                 silent no-op (no error shown)
 *   5. A selected value doesn't match any ACO  → that required field stays empty
 *      <option> (label/casing drift)             and WCPA/Shopify blocks the add
 *   6. A required ACO field is left empty       → submit blocked
 *   7. Variant unavailable / sold out          → theme disables submit
 *
 * Used two ways:
 *   • In-app: screen.jsx exposes window.WSC_DIAGNOSE() and calls this when an
 *     Add-to-Cart click fails, so real (intermittent) failures are captured
 *     instead of silently swallowed.
 *   • Standalone: shopify/wsc-cart-diagnostic.js mirrors this so it can be
 *     pasted into the browser console / used as a bookmarklet / sent to a
 *     stuck customer — even if the React bundle never loaded.
 *
 * ⚠️  This file is mirrored by  shopify/wsc-cart-diagnostic.js  (standalone,
 *     no imports). If you change the audit logic here, update that copy too.
 * ============================================================================
 */

// Exact selectors the configurator uses to find the form + submit button.
// Kept identical to screen.jsx so the diagnostic checks what actually runs.
const SELECTORS = {
  cartForm: 'form[action*="/cart/add"]',
  cartFormAlt: '.product-form',
  submitButtons: [
    'form[action*="/cart/add"] [type="submit"]',
    '.product-form__submit',
    'button[name="add"]',
  ],
  acpaHidden: 'input[name="properties[_acpaHidden]"]',
  wcpaNodes: '.wcpa_form_outer, .wcpa-form-fields, [class*="wcpa"]',
  propSelects: 'select[name^="properties["]',
  propHidden: 'input[type="hidden"][name^="properties["]',
  mount: '#wsc-configurator',
};

// Known ACO field for the acoReady gate (screen.jsx polls findAcoSelect('meshColor')).
// Used only by the standalone fallback finder; the in-app caller passes its real
// findAcoSelect via ctx.findField instead.
const GATE_FIELD = {
  name: 'properties[Mesh Color]',
  defaultId: 'field_select_519020549',
  kitId: 'field_select_662436017',
  kitHandle: 'custom-window-screen-kit',
};

const STATUS = { PASS: 'pass', FAIL: 'fail', WARN: 'warn', INFO: 'info' };

function isEffectivelyDisabled(el) {
  if (!el) return true; // no button at all → can't submit
  // IMPORTANT: the configurator deliberately hides the theme's real submit
  // button off-screen (position:absolute; left:-9999px; opacity:0;
  // pointer-events:none) and clicks it PROGRAMMATICALLY. A synthetic
  // element.click() submits the form regardless of CSS visibility /
  // pointer-events / opacity / position, so none of those block add-to-cart
  // and must NOT be treated as "disabled" (that was a false positive on every
  // page). Only a genuinely disabled control blocks a programmatic submit.
  if (el.disabled) return true;
  if (el.getAttribute && el.getAttribute('aria-disabled') === 'true') return true;
  return false;
}

// Built-in gate finder for standalone use (mirrors screen.jsx findAcoSelect for meshColor).
function builtinFindGate(productHandle) {
  if (typeof document === 'undefined') return null;
  if (productHandle === GATE_FIELD.kitHandle) {
    const byKit = document.getElementById(GATE_FIELD.kitId);
    if (byKit) return byKit;
  }
  const byName = document.querySelector(`select[name="${GATE_FIELD.name}"]`);
  if (byName) return byName;
  return document.getElementById(GATE_FIELD.defaultId);
}

/**
 * Run the full add-to-cart audit.
 *
 * @param {object} ctx
 * @param {boolean} [ctx.acoReady]            the configurator's real acoReady flag
 * @param {function} [ctx.findField]          (mappingKey) => Element|null  (screen.jsx findAcoSelect)
 * @param {object}  [ctx.mappings]            WCPA_MAPPINGS (for field names/labels)
 * @param {object}  [ctx.expectedValues]      { mappingKey: expected <option> text } for the
 *                                            CURRENT selection — enables option-drift detection
 * @param {string}  [ctx.productHandle]
 * @param {boolean} [ctx.isKit]
 * @param {string}  [ctx.trigger]             what invoked this (e.g. 'aco-not-ready', 'manual')
 * @param {boolean} [ctx.silent]              if true, don't console.log (return only)
 * @returns {{checks: Array, verdict: object, trigger: string, at: string}}
 */
export default function runCartDiagnostics(ctx = {}) {
  const checks = [];
  const add = (id, label, status, detail, fix) =>
    checks.push({ id, label, status, detail: detail || '', fix: fix || '' });

  const handle =
    ctx.productHandle ||
    (typeof window !== 'undefined' && window.WSC_CONFIG && window.WSC_CONFIG.productHandle) ||
    '';
  const findField = typeof ctx.findField === 'function' ? ctx.findField : null;

  // ---- 1. Cart-add form ----------------------------------------------------
  const form =
    document.querySelector(SELECTORS.cartForm) ||
    document.querySelector(SELECTORS.cartFormAlt);
  if (!form) {
    add('form', 'Cart form', STATUS.FAIL,
      'No <form action="/cart/add"> (or .product-form) on the page.',
      'The configurator submits by clicking the theme form\'s button. With no form there is nothing to submit into — confirm this is a product page and the theme markup hasn\'t changed.');
  } else {
    add('form', 'Cart form', STATUS.PASS, `Found: ${form.getAttribute('action') || form.className}`);
  }

  // ---- 2. Theme submit button ---------------------------------------------
  let submitBtn = null;
  for (const sel of SELECTORS.submitButtons) {
    submitBtn = document.querySelector(sel);
    if (submitBtn) break;
  }
  if (!submitBtn) {
    add('submit', 'Theme submit button', STATUS.FAIL,
      'None of [type="submit"] / .product-form__submit / button[name="add"] found.',
      'The configurator clicks the theme\'s real button to add to cart. If it can\'t find one, the Add-to-Cart button does nothing. Check the theme\'s product-form button selector.');
  } else if (isEffectivelyDisabled(submitBtn)) {
    add('submit', 'Theme submit button', STATUS.FAIL,
      `Found but truly DISABLED (<${submitBtn.tagName.toLowerCase()}> "${(submitBtn.textContent || '').trim().slice(0, 40)}").`,
      'The button carries a real disabled / aria-disabled state, which blocks the programmatic submit. Usually means the variant is sold out / unavailable, or the theme hasn\'t finished hydrating. Check inventory and variant availability.');
  } else {
    add('submit', 'Theme submit button', STATUS.PASS,
      `Found & clickable (<${submitBtn.tagName.toLowerCase()}> "${(submitBtn.textContent || '').trim().slice(0, 40)}"). The theme hides it off-screen by design; the configurator clicks it programmatically.`);
  }

  // ---- 3. ACO / WCPA app present ------------------------------------------
  const wcpaNodes = document.querySelectorAll(SELECTORS.wcpaNodes).length;
  const propSelects = document.querySelectorAll(SELECTORS.propSelects).length;
  const propHidden = document.querySelectorAll(SELECTORS.propHidden).length;
  const acpaHidden = document.querySelector(SELECTORS.acpaHidden);
  const acoPresent = wcpaNodes > 0 || propSelects > 0 || propHidden > 0 || !!acpaHidden;
  if (!acoPresent) {
    add('aco-app', 'ACO/WCPA options app', STATUS.FAIL,
      'No WCPA nodes, no properties[...] fields, no _acpaHidden on the page.',
      'The custom-options app did not load. Most common cause of intermittent failures: an ad/script blocker or privacy extension stopped the app\'s JS, or the app had an outage. Without it, required option fields never render and Add-to-Cart can\'t succeed. Ask the customer to disable extensions / try another browser, and verify the ACO app is enabled in Shopify.');
  } else {
    add('aco-app', 'ACO/WCPA options app', STATUS.PASS,
      `Present (${wcpaNodes} wcpa node(s), ${propSelects} select(s), ${propHidden} hidden input(s), _acpaHidden: ${acpaHidden ? 'yes' : 'no'}).`);
  }

  // ---- 4. acoReady gate (the critical one) --------------------------------
  const gateEl = findField ? findField('meshColor') : builtinFindGate(handle);
  const gateHidden = document.querySelector(`input[type="hidden"][name="${GATE_FIELD.name}"]`);
  if (ctx.acoReady === false) {
    add('aco-ready', 'Configurator ready (acoReady)', STATUS.FAIL,
      'acoReady is FALSE — the configurator never detected the ACO fields within its 10s window.',
      'In this state the Add-to-Cart button is a SILENT no-op. Usually a knock-on of #3 (app blocked / slow), or the ACO "Mesh Color" field was renamed so findAcoSelect can\'t locate the gate field. Hard-refresh; if it persists across refreshes, the field schema likely changed.');
  } else if (gateEl) {
    add('aco-ready', 'Configurator ready (acoReady)', STATUS.PASS,
      `Gate field "${GATE_FIELD.name}" located (#${gateEl.id || 'no-id'}) → acoReady can become true.` +
      (ctx.acoReady === true ? ' acoReady is currently TRUE.' : ''));
  } else {
    add('aco-ready', 'Configurator ready (acoReady)', STATUS.FAIL,
      `Gate field NOT found. Tried name="${GATE_FIELD.name}", id="${GATE_FIELD.defaultId}"` +
      (handle === GATE_FIELD.kitHandle ? `, kit id="${GATE_FIELD.kitId}"` : '') +
      `. A hidden input by that name ${gateHidden ? 'DOES' : 'does NOT'} exist.`,
      gateHidden
        ? 'The hidden input exists but the visible <select> the poller looks for does not — the field schema/markup changed. Update findAcoSelect / WCPA field IDs.'
        : 'Neither the select nor its hidden input exist → the "Mesh Color" ACO field is missing or renamed on this product. The configurator will never become ready and Add-to-Cart will silently do nothing.');
  }

  // ---- 5. ACO option fields: values + required-but-empty -------------------
  const allProps = Array.from(document.querySelectorAll(`${SELECTORS.propSelects}, ${SELECTORS.propHidden}`));
  const seen = new Set();
  const emptyRequired = [];
  const fieldRows = [];
  allProps.forEach((el) => {
    const name = el.getAttribute('name');
    if (!name || name === 'properties[_acpaHidden]' || seen.has(name)) return;
    seen.add(name);
    const val = (el.value || '').trim();
    const wrapper = el.closest('[class*="wcpa"], .form-field, .field') || el.parentElement;
    const required =
      el.required ||
      el.getAttribute('aria-required') === 'true' ||
      (wrapper && /required/i.test(wrapper.className || '')) ||
      (el.className && /required/i.test(el.className.toString()));
    fieldRows.push({ name, value: val || '(empty)', required: !!required });
    if (required && !val) emptyRequired.push(name);
  });
  if (emptyRequired.length) {
    add('required', 'Required option fields', STATUS.FAIL,
      `Empty but required: ${emptyRequired.join(', ')}.`,
      'WCPA/Shopify will block the add while a required option is empty. This is often a downstream symptom of option-drift (#6): the configurator wrote a value that didn\'t match any <option>, so the field stayed empty.');
  } else if (fieldRows.length) {
    add('required', 'Required option fields', STATUS.PASS,
      `${fieldRows.length} option field(s) present; no required field is empty.`);
  } else {
    add('required', 'Required option fields', STATUS.WARN,
      'No properties[...] option fields found to inspect (see #3).');
  }

  // ---- 6. Value -> <option> drift (needs the live configurator selection) --
  if (ctx.expectedValues && findField) {
    const mismatches = [];
    Object.keys(ctx.expectedValues).forEach((key) => {
      const expected = ctx.expectedValues[key];
      if (expected == null || expected === '') return; // intentionally-empty (e.g. crossbar none)
      const el = findField(key);
      if (!el || el.tagName !== 'SELECT' || !el.options) return; // react-select / hidden-only — skip
      const has = Array.from(el.options).some((o) => o.value === expected);
      if (!has) {
        mismatches.push({
          field: (ctx.mappings && ctx.mappings[key] && ctx.mappings[key].selectName) || key,
          wanted: expected,
          available: Array.from(el.options).map((o) => o.value),
        });
      }
    });
    if (mismatches.length) {
      add('drift', 'Selection → ACO option match', STATUS.FAIL,
        mismatches.map((m) => `"${m.field}" wants "${m.wanted}" but options are [${m.available.map((v) => `"${v}"`).join(', ')}]`).join(' | '),
        'A configurator choice does not match any live ACO <option> (label/casing drift). That field can\'t be set, stays empty, and blocks the add. Fix the value in WCPA_MAPPINGS / PRODUCT_VALUE_OVERRIDES to match the live option text exactly.');
    } else {
      add('drift', 'Selection → ACO option match', STATUS.PASS,
        'Every current selection maps to an existing ACO <option>.');
    }
  } else {
    add('drift', 'Selection → ACO option match', STATUS.INFO,
      'Skipped — run from the live configurator (window.WSC_DIAGNOSE()) to verify the current selection maps to real ACO options.');
  }

  // ---- 7. _acpaHidden state blob ------------------------------------------
  if (acpaHidden) {
    let ok = true;
    try { JSON.parse(acpaHidden.value || '{}'); } catch (e) { ok = false; }
    add('acpa', '_acpaHidden state blob', ok ? STATUS.PASS : STATUS.WARN,
      ok ? 'Present and valid JSON.' : 'Present but not valid JSON — WCPA may recompute/clear fields on submit.',
      ok ? '' : 'Corrupt _acpaHidden can cause WCPA to reset fields on submit. Reload the page.');
  } else if (acoPresent) {
    add('acpa', '_acpaHidden state blob', STATUS.WARN,
      'ACO fields exist but properties[_acpaHidden] is missing.',
      'Some WCPA versions reconcile field values from this blob on submit; its absence can drop synced values.');
  }

  // ---- 8. Configurator mount ----------------------------------------------
  const mount = document.querySelector(SELECTORS.mount);
  if (!mount) {
    add('mount', 'Configurator mount', STATUS.WARN,
      'No #wsc-configurator element.',
      'The configurator fell back to injecting itself before the cart button, or it isn\'t on this page.');
  } else {
    add('mount', 'Configurator mount', mount.childElementCount > 0 ? STATUS.PASS : STATUS.FAIL,
      mount.childElementCount > 0 ? 'Mounted with rendered content.' : 'Mount element exists but is EMPTY — the React bundle failed to render.',
      mount.childElementCount > 0 ? '' : 'The wsc-widget bundle didn\'t render. Check the browser console for a script error / 404 on the widget JS.');
  }

  // ---- Verdict: most likely cause, in priority order ----------------------
  const fails = checks.filter((c) => c.status === STATUS.FAIL);
  const byId = (id) => checks.find((c) => c.id === id);
  const priority = ['form', 'submit', 'aco-app', 'aco-ready', 'drift', 'required', 'mount'];
  let primary = null;
  for (const id of priority) {
    const c = byId(id);
    if (c && c.status === STATUS.FAIL) { primary = c; break; }
  }

  const USER_MESSAGES = {
    form: 'This doesn\'t look like a valid product page right now. Please reload and try again.',
    submit: 'This item may be unavailable right now. Please reload, and if it persists contact us.',
    'aco-app': 'A required part of the page didn\'t load — often caused by an ad/privacy blocker. Please disable extensions or try another browser, then reload.',
    'aco-ready': 'The configurator is still loading its options. Please reload the page and try again.',
    drift: 'One of your selections couldn\'t be applied. Please reload and re-pick your options.',
    required: 'A required option wasn\'t set. Please reload and complete all selections.',
    mount: 'The configurator didn\'t finish loading. Please reload the page.',
  };

  const verdict = primary
    ? {
        ok: false,
        cause: primary.id,
        title: primary.label,
        summary: primary.detail,
        fix: primary.fix,
        userMessage: USER_MESSAGES[primary.id] || 'Something went wrong adding this to your cart. Please reload and try again.',
        otherFailures: fails.filter((c) => c.id !== primary.id).map((c) => c.id),
      }
    : {
        ok: true,
        cause: null,
        title: 'No blocking problem detected',
        summary: 'Every link in the add-to-cart chain passed. If a customer still can\'t add to cart, capture this report on THEIR browser (the failure is environment-specific — device, extension, or network).',
        fix: '',
        userMessage: '',
        otherFailures: [],
      };

  const report = { trigger: ctx.trigger || 'manual', at: new Date().toISOString(), handle, checks, verdict };

  if (typeof window !== 'undefined') window.__WSC_LAST_DIAGNOSIS = report;
  if (!ctx.silent) printReport(report);
  return report;
}

/**
 * Fire-and-forget telemetry when a REAL add-to-cart click fails, so failures
 * from real customers are captured automatically — no customer action, no
 * "please paste this in your console". Sends to whatever the store already has
 * loaded (each path is a graceful no-op if that tool isn't present):
 *
 *   • GA4 / gtag           → event "wsc_add_to_cart_failed"
 *   • GTM dataLayer        → push { event: "wsc_add_to_cart_failed", ... }
 *   • Microsoft Clarity    → tags the session "wsc_cart_failure" = <cause>
 *                            (so you can filter session replays to the exact
 *                            broken sessions and watch what happened)
 *   • First-party beacon   → navigator.sendBeacon(window.WSC_CONFIG.failureBeaconUrl)
 *                            ONLY if the merchant set that URL (your endpoint).
 *
 * Payload is PII-free: option/verdict metadata + URL + userAgent only. No
 * names, emails, addresses, or the customer's free-text comments.
 */
export function emitFailureTelemetry(report, ctx = {}) {
  if (typeof window === 'undefined') return null;
  const v = (report && report.verdict) || {};
  const payload = {
    event: 'wsc_add_to_cart_failed',
    cause: v.cause || 'unknown',
    verdict: v.title || '',
    product: ctx.isKit ? 'wsc-kit' : 'wsc',
    acoReady: !!ctx.acoReady,
    handle: (report && report.handle) || (window.WSC_CONFIG && window.WSC_CONFIG.productHandle) || '',
    trigger: (report && report.trigger) || '',
    otherFailures: (v.otherFailures || []).join(','),
    url: typeof location !== 'undefined' ? location.href : '',
    ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    at: (report && report.at) || new Date().toISOString(),
  };
  try { if (typeof window.gtag === 'function') window.gtag('event', payload.event, payload); } catch (e) { /* noop */ }
  try { if (Array.isArray(window.dataLayer)) window.dataLayer.push({ ...payload }); } catch (e) { /* noop */ }
  try {
    if (typeof window.clarity === 'function') {
      window.clarity('set', 'wsc_cart_failure', payload.cause);
      window.clarity('event', 'wsc_add_to_cart_failed');
    }
  } catch (e) { /* noop */ }
  try {
    const url = window.WSC_CONFIG && window.WSC_CONFIG.failureBeaconUrl;
    if (url && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    }
  } catch (e) { /* noop */ }
  return payload;
}

// Pretty console output (grouped, color-coded). Falls back to plain logs.
export function printReport(report) {
  if (typeof console === 'undefined') return;
  const ICON = { pass: '✓', fail: '✗', warn: '!', info: 'i' };
  const COLOR = { pass: '#16a34a', fail: '#dc2626', warn: '#d97706', info: '#2563eb' };
  const header = report.verdict.ok
    ? '%c WSC Add-to-Cart Diagnostic — no blocking problem found '
    : '%c WSC Add-to-Cart Diagnostic — PROBLEM FOUND ';
  const headerStyle = report.verdict.ok
    ? 'background:#16a34a;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px'
    : 'background:#dc2626;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px';
  try { console.groupCollapsed(header, headerStyle); } catch (e) { console.log(header.replace('%c', ''), report.verdict.title); }
  report.checks.forEach((c) => {
    console.log(
      `%c${ICON[c.status]} %c${c.label}%c — ${c.detail}`,
      `color:${COLOR[c.status]};font-weight:bold`,
      'font-weight:bold',
      'color:inherit'
    );
    if (c.status === 'fail' && c.fix) console.log(`   ↳ Fix: ${c.fix}`);
  });
  console.log('%cVERDICT:', 'font-weight:bold;text-decoration:underline');
  console.log(`  ${report.verdict.title}${report.verdict.cause ? ` (cause: ${report.verdict.cause})` : ''}`);
  if (report.verdict.summary) console.log(`  ${report.verdict.summary}`);
  if (report.verdict.fix) console.log(`  → ${report.verdict.fix}`);
  if (report.verdict.otherFailures && report.verdict.otherFailures.length)
    console.log(`  Also failing: ${report.verdict.otherFailures.join(', ')}`);
  try { console.groupEnd(); } catch (e) { /* noop */ }
}
